import * as React from "react";
import Api from "./Api";
import "./CampusPage.scss";
import LookupCtl from "./input/LookupCtl";
import LabelCtl from "./input/LabelCtl";
import SelectCtl from "./input/SelectCtl";
import TextCtl from "./input/TextCtl";
import PostalAddressCtl from "./PostalAddressCtl"
import { Company } from "@testout/testout-commerce/models/hubspot/Company";
import { Campus } from "@testout/testout-commerce/models/labsim/Campus";
import { PostalAddress } from "@testout/testout-commerce/models/commerce/PostalAddress";
import { ListItem } from "@testout/testout-commerce/models/ListItem";
import Segment from "@testout/testout-commerce/models/Segment";


interface CampusPageProps {
    companyId?: string;
    campusProfileId?: string;
}

interface CampusPageState {
    initialized: boolean;
    inProgress: string;
    errorMessage: string;
    campusName?: string;
    campusDisplayStyle?: string;
    campusPostal?: PostalAddress;
    campusId?: string;
    company?: Company;

    showAssociateDlg: boolean;
    associateCampusProfileId?: string;
}

export default class CampusPage extends React.PureComponent<CampusPageProps, CampusPageState> {
    constructor(props: CampusPageProps) {
        super(props);
        this.state = {
            initialized: false, inProgress: "", errorMessage: "",
            campusDisplayStyle: "", campusName: "",
            showAssociateDlg: false
        };
    }

    private _displayStyle: ListItem[] = [
        { name: "School", value: "SCHOOL" },
        { name: "Campus", value: "CAMPUS" },
        { name: "Company", value: "COMPANY" }
    ];

    async componentDidMount() {
        // Parse the URL:
        // campus?companyId=xxxx  - Associate a new or existing campus with HS company ID xxxx
        // campus/xxxx            - Bring up campus xxxx from LabSim
        // campus                 - Bring up a blank campus page

        if (this.props.companyId) {
            let company = await Api.get<Company>(`/api/companies/${this.props.companyId}`);
            this.setState({ initialized: true, showAssociateDlg: true, company: company, associateCampusProfileId: company?.labsim6_companyprofileid });
        }
        else if (this.props.campusProfileId)
            this.loadCampus();
        else
            this.initCampus();
    }

    // Initialize the campus fields from company or default values
    initCampus = async () => {
        try {
            this.setState({ errorMessage: "", inProgress: "Initializing from company..." });

            let company = this.state.company;
            let segment = new Segment(company?.segment ?? "EDU-2YR");

            let campusDisplayStyle = segment.parentSegment?.code === Segment.higherEd.code ? "CAMPUS" :
                                     segment.parentSegment?.code === Segment.secondaryEd.code ? "SCHOOL" : "COMPANY";
            let campusName = company?.name ?? "";
            let campusPostal = new PostalAddress({
                line1: company?.address,
                line2: company?.address2,
                city: company?.city,
                stateOrProvince: company?.state,
                postalCode: company?.zip,
                countryCode: company?.country ?? "USA"
            });

            this.setState({
                initialized: true, showAssociateDlg: false, campusDisplayStyle: campusDisplayStyle,
                campusName: campusName, campusPostal: campusPostal, inProgress: ""
            });
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while loading the HubSpot company information: ${error.message}`, inProgress: "" });
        }
    }

    // Load an existing campus from LabSim
    loadCampus = async () => {
        try {
            let campus: Campus = await Api.get(`/api/campuses/${this.props.campusProfileId}`);

            this.setState({
                initialized: true, campusName: campus.campusName, campusDisplayStyle: campus.campusDisplayStyle, campusId: campus.campusId,
                campusPostal: new PostalAddress({
                    line1: campus.address,
                    city: campus.city,
                    stateOrProvince: campus.region,
                    postalCode: campus.postalCode,
                    countryCode: campus.country ?? "USA"
                })
            });
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while loading the institution: ${error.message}`, initialized: true });
        }
    }

    // Save the current customer to the Commerce Server
    saveCampus = async () => {
        try {
            // Validate that necessary fields have values
            let msg = "";
            if (!this.state.campusName)
                msg += " Please enter an institution name.";
            if (this.state.campusId)
                msg += " Updating a CertMaster Institution is not currectly supported."
            if (msg) {
                this.setState({ errorMessage: msg });
                return;
            }

            this.setState({ errorMessage: "", inProgress: `Saving${this.props.companyId ? " and associating" : ""} CertMaster Institution...` });

            let campus = await Api.post<Campus>("/api/campuses", {
                campusName: this.state.campusName ?? "",
                address: `${this.state.campusPostal?.line1 ?? ""} ${this.state.campusPostal?.line2 ?? ""} ${this.state.campusPostal?.line3 ?? ""}`.trimEnd(),
                city: this.state.campusPostal?.city,
                region: this.state.campusPostal?.stateOrProvince,
                postalCode: this.state.campusPostal?.postalCode,
                country: this.state.campusPostal?.countryCode,
                campusDisplayStyle: this.state.campusDisplayStyle ?? "SCHOOL",
            });
            if (this.props.companyId)
                await Api.put<object>(`/api/companies/${this.props.companyId}/campus/${campus.campusId}/association`, null);

            this.setState({ inProgress: "" });
            this.closeWindow();
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while saving campus: ${error.message}`, inProgress: "" });
        }
    }

    // Associate the company with the campus in HubSpot
    linkCampus = async () => {
        try {
            if (!this.state.associateCampusProfileId)
                throw new Error("Please select an institution before associating.");

            this.setState({ errorMessage: "", inProgress: "Associating institution..." });

            if (this.props.companyId)
                await Api.put<object>(`/api/companies/${this.props.companyId}/campus/${this.state.associateCampusProfileId}/association`, null);

            this.setState({ inProgress: "" });
            this.closeWindow();
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while associating institution: ${error.message}`, inProgress: "" });
        }
    }

    onAssociateCampusSearch = async (value: string, exact?: boolean): Promise<ListItem[]> => {
        return exact
            ? [await Api.get<ListItem>(`/api/campuses/${value}/list-item`)]
            : await Api.get<ListItem[]>(`/api/campuses/list-items?search=${value}`);
    }

    onAssociateCampusChange = (value: string) => {
        this.setState({ associateCampusProfileId: value });
    }

    onCampusSelect = async (value: string, exact?: boolean): Promise<ListItem[]> => {
        return exact
            ? [await Api.get<ListItem>(`/api/campuses/${value}/list-item`)]
            : await Api.get<ListItem[]>(`/api/campuses/list-items?search=${value}`);
    }

    onNameChange = (value: string) => {
        this.setState({ campusName: value });
    }

    onDisplayStyleChange = (value: string) => {
        this.setState({ campusDisplayStyle: value });
    }

    onPostalChange = (postal: PostalAddress) => {
        this.setState({ campusPostal: postal });
    }

    closeWindow = () => {
        window.parent.postMessage(JSON.stringify({ action: "CANCEL" }), "*");
    }

    render() {
        if (!this.state.initialized)
            return <div className="page CampusPage loading"><div className="spinner"></div></div>;

        if (this.state.showAssociateDlg)
            return <div className="page CampusPage">
                <header>
                    <p>Select the existing CertMaster Institution to associate with company <span className="bold">{this.state.company?.name}</span> located
                        in {this.state.company?.city}{this.state.company?.state ? ", " + this.state.company?.state : ""}{this.state.company?.country !== "USA" ? " " + this.state.company?.country : ""}.
                        If a matching CertMaster Institution does not exist, click <span className="italic">Associate with new CertMaster Institution</span> below to create and associate a new one.
                        </p>
                    {this.state.errorMessage &&
                        <p className="error">{this.state.errorMessage}</p>}
                </header>
                <main>
                   <section className="common">
                        <LabelCtl label="LabSim Campus">
                            <LookupCtl className="associate" placeholder="Search CertMaster Institutions" disabled={false} value={this.state.associateCampusProfileId} onSearch={this.onAssociateCampusSearch}
                                onChange={this.onAssociateCampusChange} alwaysOpen={true} hideItemValue={true} initialSearchValue={this.state.company?.name} />
                       </LabelCtl>
                   </section>
                </main>

                <footer>
                    <button className="tertiary" onClick={() => this.initCampus()}>Associate with new CertMaster Institution</button>
                    <button className="secondary" onClick={() => this.closeWindow()}>Cancel</button>
                    <button className="primary" onClick={() => this.linkCampus()}>Associate</button>
                </footer>

                {this.state.inProgress &&
                    <div className="saving"><div><div>{this.state.inProgress}</div><div className="spinner"></div></div></div>}
            </div>;

        return <div className="page CampusPage">
           <header>
               <div className="title">
                    <h3>{!this.state.campusId ? " -- New Campus -- " : this.state.campusName}</h3>
                    <h6>CertMaster Institution</h6>
               </div>
               <div className="actions">
                </div>
               {this.state.errorMessage &&
                    <p className="error">{this.state.errorMessage}</p>}
           </header>

           <main>
               <section className="full">
                    <LabelCtl label="Name">
                        <TextCtl value={this.state.campusName} disabled={false} onChange={this.onNameChange} />
                   </LabelCtl>
                    <LabelCtl label="Display Style">
                        <SelectCtl value={this.state.campusDisplayStyle} disabled={false} items={this._displayStyle} hideItemValue={true} onChange={this.onDisplayStyleChange} />
                    </LabelCtl>
                    <LabelCtl label="Address" useDiv={true}>
                        <PostalAddressCtl disabled={false} postal={this.state.campusPostal ?? new PostalAddress({ countryCode: "USA" })}
                            onChange={this.onPostalChange} defaultCountryCode="USA" />
                    </LabelCtl>
               </section>
           </main>

            <footer>
                <button className="secondary" onClick={() => this.closeWindow()}>{this.props.companyId ? "Cancel" : "Close"}</button>
                <button className="primary" disabled={!!this.state.inProgress} onClick={() => this.saveCampus()}>Save{this.props.companyId ? " and Associate" : ""}</button>
           </footer>

            {this.state.inProgress &&
                <div className="saving"><div><div>{this.state.inProgress}</div><div className="spinner"></div></div></div>}
        </div>;
    }
}
